import { useState, useEffect } from 'react';
import { getPointsBalance, getPointsLogs } from '@/api/user';
import type { PointsLog } from '@/api/user';
import { getMinPointsCost } from '@/api/config';
import { useAppConfig } from '@/hooks/useAppConfig';

interface PointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPoints: number;
}

interface DisplayLog {
  id: string;
  type: 'earn' | 'consume';
  desc: string;
  descKey?: string;
  points: number;
  createdAt: string;
}

const REASON_KEY_MAP: Record<string, string> = {
  register_gift: 'pointsLog_register',
  generate_cost: 'pointsLog_generate',
  refund_gen_fail: 'pointsLog_refundGenFail',
  vip_gift: 'pointsLog_vipGift',
  prompt_expand_cost: 'pointsLog_promptExpandCost',
  daily_login: 'pointsLog_dailyLogin',
};

function formatDate(isoStr: string): string {
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return isoStr;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toDisplayLog(log: PointsLog): DisplayLog {
  return {
    id: log.id,
    type: log.delta > 0 ? 'earn' : 'consume',
    desc: log.reason,
    descKey: REASON_KEY_MAP[log.reason],
    points: log.delta,
    createdAt: formatDate(log.created_at),
  };
}

const FALLBACK_LOGS: DisplayLog[] = [
  { id: 'fl_001', type: 'consume', desc: 'generate_cost', descKey: 'pointsLog_generate', points: -2, createdAt: '2026-04-23 14:32' },
  { id: 'fl_002', type: 'earn', desc: 'daily_login', descKey: 'pointsLog_dailyLogin', points: 1, createdAt: '2026-04-23 09:00' },
  { id: 'fl_003', type: 'earn', desc: 'register_gift', descKey: 'pointsLog_register', points: 6, createdAt: '2026-04-22 10:00' },
];

export default function PointsModal({ isOpen, onClose, currentPoints }: PointsModalProps) {
  const config = useAppConfig();
  const pointsRules = config?.points_rules;
  const minGenerationCost = getMinPointsCost(config);
  const { t } = useTranslation();
  const [logs, setLogs] = useState<DisplayLog[]>([]);
  const [balance, setBalance] = useState<number>(currentPoints);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const [balanceRes, logsRes] = await Promise.all([
          getPointsBalance(),
          getPointsLogs(1, 20),
        ]);
        if (!cancelled) {
          setBalance(balanceRes.balance);
          setLogs(logsRes.items.map(toDisplayLog));
        }
      } catch {
        if (!cancelled) {
          setBalance(currentPoints);
          setLogs(FALLBACK_LOGS);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [isOpen, currentPoints]);

  if (!isOpen) return null;

  const displayLogs = logs.length > 0 ? logs : FALLBACK_LOGS;

  return (
    <div className="fixed inset-0 z-50 flex items-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full bg-white rounded-t-3xl max-h-[75vh] flex flex-col animate-slide-up">
        {/* 头部 */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{t('profile_pointsTitle')}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{t('member_currentPoints')}{balance}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        {/* 积分规则 */}
        <div className="px-5 mb-4 flex-shrink-0">
          <div className="rounded-2xl p-4 grid grid-cols-3 gap-3"
            style={{ background: 'linear-gradient(135deg, #FFF0F6, #F5EEFF)' }}>
            {[
              { icon: 'ri-user-add-line', label: t('points_rule_register'), value: pointsRules?.register_gift !== undefined ? `+${pointsRules.register_gift}` : '-', color: '#FF6B9D' },
              { icon: 'ri-calendar-check-line', label: t('points_rule_dailyLogin'), value: pointsRules?.daily_login !== undefined ? `+${pointsRules.daily_login}` : '-', color: '#A855F7' },
              { icon: 'ri-sparkling-2-line', label: t('points_rule_generate'), value: minGenerationCost ? `-${minGenerationCost}` : '--', color: '#6366F1' },
            ].map((rule) => (
              <div key={rule.label} className="flex flex-col items-center gap-1.5 bg-white rounded-xl p-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                  style={{ background: `${rule.color}18` }}>
                  <i className={`${rule.icon} text-sm`} style={{ color: rule.color }}></i>
                </div>
                <span className="text-xs font-bold" style={{ color: rule.color }}>{rule.value}</span>
                <span className="text-xs text-gray-500">{rule.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 流水列表 */}
        <div className="px-5 pb-2 flex-shrink-0">
          <p className="text-xs font-semibold text-gray-500 mb-3">{t('profile_pointsLog')}</p>
        </div>
        <div className={`overflow-y-auto flex-1 px-5 pb-8 transition-opacity ${loading ? 'opacity-50' : 'opacity-100'}`}>
          <div className="space-y-2">
            {displayLogs.map((log) => (
              <div key={log.id} className="flex items-center justify-between py-3 border-b border-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: log.type === 'earn' ? '#F0FDF4' : '#FFF0F6' }}>
                    <i className={`${log.type === 'earn' ? 'ri-add-circle-line text-green-500' : 'ri-sparkling-2-line text-pink-400'} text-base`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{log.descKey ? t(log.descKey) : log.desc}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{log.createdAt}</p>
                  </div>
                </div>
                <span className={`text-base font-bold ${log.type === 'earn' ? 'text-green-500' : 'text-gray-700'}`}>
                  {log.points > 0 ? `+${log.points}` : log.points}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
